import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type GlobalPageLoaderApi = {
  /** Increment loading depth; overlay shows when depth is greater than 0 */
  begin: () => void;
  /** Decrement loading depth (floored at 0) */
  end: () => void;
};

const GlobalPageLoaderContext = createContext<GlobalPageLoaderApi | null>(null);

export function GlobalPageLoaderProvider({ children }: { children: React.ReactNode }) {
  const [depth, setDepth] = useState(0);

  const begin = useCallback(() => {
    setDepth((d) => d + 1);
  }, []);

  const end = useCallback(() => {
    setDepth((d) => Math.max(0, d - 1));
  }, []);

  const value = useMemo(() => ({ begin, end }), [begin, end]);

  return (
    <GlobalPageLoaderContext.Provider value={value}>
      {children}
      {depth > 0 ? <div className="loader-overlay" aria-busy="true" /> : null}
    </GlobalPageLoaderContext.Provider>
  );
}

export function useGlobalPageLoader(): GlobalPageLoaderApi {
  const ctx = useContext(GlobalPageLoaderContext);
  if (!ctx) {
    throw new Error("useGlobalPageLoader must be used within GlobalPageLoaderProvider");
  }
  return ctx;
}

/** Keeps global loader in sync with a boolean loading flag (e.g. SWR isLoading). */
export function useSyncGlobalPageLoader(active: boolean): void {
  const { begin, end } = useGlobalPageLoader();
  useEffect(() => {
    if (!active) return;
    begin();
    return () => {
      end();
    };
  }, [active, begin, end]);
}
